import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const MissingFilesSection = ({ missingLines, playFilter }) => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('scene');
  const [filterCharacter, setFilterCharacter] = useState('');

  const groupedByScene = useMemo(() => {
    const grouped = {};
    
    missingLines?.forEach(line => {
      const sceneKey = `${line?.plays?.title} - Act ${line?.act}, Scene ${line?.scene}`;
      if (!grouped?.[sceneKey]) {
        grouped[sceneKey] = {
          playTitle: line?.plays?.title,
          act: line?.act,
          scene: line?.scene,
          lines: [],
          characters: new Set()
        };
      }
      grouped?.[sceneKey]?.lines?.push(line);
      grouped?.[sceneKey]?.characters?.add(line?.speaker);
    });

    return Object.entries(grouped)?.map(([key, data]) => ({
      sceneKey: key,
      ...data,
      characterCount: data?.characters?.size,
      lineCount: data?.lines?.length,
      urgency: data?.lines?.length > 50 ? 'high' : data?.lines?.length > 20 ? 'medium' : 'low'
    }));
  }, [missingLines]);

  const filteredScenes = useMemo(() => {
    let filtered = groupedByScene;

    if (filterCharacter) {
      filtered = filtered?.filter(scene => 
        Array.from(scene?.characters)?.some(char => 
          char?.toLowerCase()?.includes(filterCharacter?.toLowerCase())
        )
      );
    }

    return filtered?.sort((a, b) => {
      if (sortBy === 'scene') return a?.sceneKey?.localeCompare(b?.sceneKey);
      if (sortBy === 'urgency') {
        const urgencyOrder = { high: 0, medium: 1, low: 2 };
        return urgencyOrder?.[a?.urgency] - urgencyOrder?.[b?.urgency];
      }
      if (sortBy === 'lines') return b?.lineCount - a?.lineCount;
      return 0;
    });
  }, [groupedByScene, sortBy, filterCharacter]);

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high':
        return 'bg-error/10 border-error text-error';
      case 'medium':
        return 'bg-warning/10 border-warning text-warning';
      case 'low':
        return 'bg-success/10 border-success text-success';
      default:
        return 'bg-muted/10 border-border text-muted-foreground';
    }
  };

  const handleNavigateToEdit = (playTitle, act, scene) => {
    navigate(`/edit-play?play=${encodeURIComponent(playTitle)}&act=${act}&scene=${scene}`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-card-foreground">Missing Audio Files by Scene</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="scene">Scene</option>
              <option value="urgency">Urgency</option>
              <option value="lines">Line Count</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Search" size={16} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Filter by character..."
              value={filterCharacter}
              onChange={(e) => setFilterCharacter(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary w-48"
            />
          </div>
        </div>
      </div>
      {filteredScenes?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
          <p className="text-lg font-semibold text-card-foreground mb-2">All Audio Files Complete!</p>
          <p className="text-muted-foreground">No missing audio files for the selected play.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredScenes?.map((scene, index) => (
            <div
              key={index}
              className="border border-border rounded-lg p-4 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-card-foreground text-lg mb-1">{scene?.sceneKey}</h3>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="FileAudio" size={14} />
                      {scene?.lineCount} missing lines
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Users" size={14} />
                      {scene?.characterCount} characters
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`px-3 py-1 rounded-full border text-xs font-medium ${getUrgencyColor(scene?.urgency)}`}>
                    {scene?.urgency?.toUpperCase()} PRIORITY
                  </div>
                  <button
                    onClick={() => handleNavigateToEdit(scene?.playTitle, scene?.act, scene?.scene)}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
                  >
                    <Icon name="Edit" size={16} />
                    Edit Scene
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {Array.from(scene?.characters)?.slice(0, 5)?.map((character, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs"
                  >
                    {character}
                  </span>
                ))}
                {scene?.characterCount > 5 && (
                  <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs">
                    +{scene?.characterCount - 5} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MissingFilesSection;